/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { GoogleMapsAPILoader } from '@/components/content/GoogleMapsAPILoader';
import { PreviewCommunication } from '@/components/content/PreviewCommunication';
import { Settings } from '@/data/Settings';
import { EventsProvider } from '@/data/context/events';
import { NotificationsProvider } from '@/data/context/notifications';
import { SessionErrorProvider } from '@/data/context/sessionError';
import { EMPTY_SETTINGS, SettingProvider } from '@/data/context/setting';
import { StoreInventoryProvider } from '@/data/context/storeInventory';
import { CookiesProvider } from '@/data/cookie/cookiesProvider';
import { getGTMConfig, initializeGTM } from '@/data/events/handlers/gtm';
import { StateProvider } from '@/data/state/provider';
import type { AppProps } from 'next/app';
import Script from 'next/script';
import 'polyfill';
import React, { useEffect, useState } from 'react';
import '../presentation/styles/custom/Intertoys.css';
import { AppCacheProvider } from '@mui/material-nextjs/v13-pagesRouter';
import '../presentation/styles/custom/magiczoomplus.css';
import reportAccessibility from '@/data/utils/reportAccessibility';
import { stripHash } from '@/data/utils/stripHash';
import { DeviceProvider, DeviceType } from '@/data/context/deviceType';

type pageProps = {
	fallback: any;
	settings: Settings;
	csrSession: boolean;
	deviceType:DeviceType;
};

interface MyAppProps extends AppProps<pageProps> {
	settings: Settings;
}

const MyApp = (props: MyAppProps) => {
	const { Component, pageProps, router } = props;
	const { deviceType } = pageProps;
	// store setting only need to fetch once ideally
	const [settings, setSettings] = useState<Settings>(() => pageProps.settings);
	const path = (router?.asPath || '').split('/');
	const stlocPage = path?.at(1) === 'winkel-zoeken';
	useEffect(() => {
		setSettings((prevSettings) => prevSettings ?? pageProps.settings);

		// ---------------------------------------------------------------------------//
		// Google Tag Manager Integration
		// ---------------------------------------------------------------------------//
		// Right now - the configuration we need from STORECONF isn't being returned:
		// Query: select * from storeconf where storeent_id=41
		// 1. 'google.analytics.versions': 'UA,GA4'
		// 2. 'google.tag.manager.auth': 'cnzsj5FrFmQu8pJAeDsjbQ'
		// 3. 'google.tag.manager.container.id': 'GTM-5HTXBL2'
		// 4. 'google.tag.manager.preview': 'env-1'
		//
		const _settings = settings ?? pageProps.settings;
		const { ua, ga4, gtmId, gtmAuth, gtmPreview } = getGTMConfig(_settings);
		if (ua || ga4) {
			initializeGTM(gtmId, gtmAuth, gtmPreview);
		}
	}, [pageProps, settings]);

	useEffect(() => {
		// Disable Next.js prefetch globally
		router.prefetch = async () => {
			// intentionally left blank
		};

		// --- Monkey-patch push and replace to strip hash fragments globally ---
		const originalPush = router.push;
		const originalReplace = router.replace;

		router.push = ((url: any, as?: any, options?: any) =>
			originalPush.call(router, stripHash(url), as, options)) as typeof router.push;

		router.replace = ((url: any, as?: any, options?: any) =>
			originalReplace.call(router, stripHash(url), as, options)) as typeof router.replace;

		// Optional cleanup (rarely needed, but safe)
		return () => {
			router.push = originalPush;
			router.replace = originalReplace;
		};
	}, [router]);

	return (
		<AppCacheProvider {...props}>
			<SettingProvider value={settings ?? EMPTY_SETTINGS}>
				{settings?.csrSession ? <Script src="/iframeResizer.contentWindow.min.js" /> : null}
				<CookiesProvider>
					<StateProvider>
						{settings?.inPreview ? <PreviewCommunication /> : null}
						{stlocPage ? <GoogleMapsAPILoader /> : null}
						<EventsProvider>
							<SessionErrorProvider>
								<NotificationsProvider>
									<DeviceProvider value={deviceType}>
										<StoreInventoryProvider>
											<Component {...pageProps} />
										</StoreInventoryProvider>
									</DeviceProvider>
								</NotificationsProvider>
							</SessionErrorProvider>
						</EventsProvider>
					</StateProvider>
				</CookiesProvider>
			</SettingProvider>
		</AppCacheProvider>
	);
};
reportAccessibility(React);
export default MyApp;
